<template>
    <div class="twofa-page">
        <div class="twofa-page__bg" aria-hidden="true">
            <div class="twofa-page__bg-circle twofa-page__bg-circle--teal"></div>
            <div class="twofa-page__bg-circle twofa-page__bg-circle--salmon"></div>
        </div>

        <div class="twofa-container">
            <div class="twofa-shield">
                <img :src="assetUrl('assets/img/escudogobe.png')" class="twofa-shield__img" alt="Escudo Tesoreria del Tolima" />
            </div>

            <h1 class="heading--1 font-signautre">Tesoreria del Tolima</h1>
            <p class="twofa-heading">Verificacion de Identidad</p>

            <div class="twofa-card">
                <div class="twofa-tabs">
                    <button type="button" :class="['twofa-tabs__item', { 'twofa-tabs__item--active': selectedMethod === 'email' }]" @click="switchMethod('email')">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <rect x="2" y="4" width="20" height="16" rx="2" />
                            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                        </svg>
                        <span class="twofa-tabs__label--full">Codigo por Correo</span>
                        <span class="twofa-tabs__label--short">Correo</span>
                    </button>
                    <button type="button" :class="['twofa-tabs__item', { 'twofa-tabs__item--active': selectedMethod === 'authenticator' }]" @click="switchMethod('authenticator')">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                            <line x1="12" y1="18" x2="12.01" y2="18" />
                        </svg>
                        <span class="twofa-tabs__label--full">Google Authenticator</span>
                        <span class="twofa-tabs__label--short">Authenticator</span>
                    </button>
                </div>

                <div v-if="selectedMethod === 'email'" class="twofa-body">
                    <template v-if="!emailCodeSent">
                        <p class="twofa-info">
                            Se enviara un codigo de 8 digitos a <strong>{{ maskedEmail }}</strong>
                        </p>
                        <button type="button" class="twofa-btn" :disabled="loading" @click="sendEmailCode" >
                            <span v-if="loading" class="twofa-spinner"></span>
                            {{ loading ? 'Enviando...' : 'Enviar Codigo' }}
                        </button>
                    </template>

                    <template v-if="emailCodeSent">
                        <p class="twofa-info">
                          Ingresa el codigo de 8 digitos enviado a <strong>{{ maskedEmail || '***@***.com' }}</strong>
                        </p>
                        <div class="otp-group">
                            <input v-for="(digit, index) in emailOtpDigits" :key="'email-' + index" :ref="el => { if (el) emailInputRefs[index] = el }" type="text" maxlength="1" class="otp-input" :value="emailOtpDigits[index]" @input="handleEmailOtpInput($event, index)" @keydown="handleEmailOtpKeydown($event, index)" @paste="handleEmailOtpPaste($event)" inputmode="numeric" autocomplete="one-time-code" :aria-label="'Digito ' + (index + 1) + ' de 8'"/>
                        </div>

                        <button type="button" class="twofa-btn" :disabled="loading || emailOtpCode.length !== 8" @click="verifyEmailCode">
                            <span v-if="loading" class="twofa-spinner"></span>
                            {{ loading ? 'Verificando...' : 'Verificar Codigo' }}
                        </button>

                        <p class="twofa-countdown">
                            <template v-if="resendCountdown > 0"> Reenvio automatico en: <strong>{{ formattedCountdown }}</strong></template>
                            <button v-else type="button" class="twofa-link-btn" @click="sendEmailCode"> Reenviar codigo </button>
                        </p>
                    </template>
                </div>

                <div v-if="selectedMethod === 'authenticator'" class="twofa-body">
                    <template v-if="!hasAuthenticator">
                        <template v-if="!qrCodeUrl">
                            <p class="twofa-info">Configura Google Authenticator para tu cuenta</p>
                            <button type="button" class="twofa-btn" :disabled="loading" @click="generateTotp">
                                <span v-if="loading" class="twofa-spinner"></span>
                                {{ loading ? 'Generando...' : 'Generar codigo QR' }}
                            </button>
                        </template>

                        <template v-if="qrCodeUrl">
                            <p class="twofa-info">Escanea el codigo QR con Google Authenticator e ingresa el codigo de 6 digitos</p>
                            <div class="qr-box">
                                <img :src="qrCodeUrl" alt="QR Google Authenticator" class="qr-box__img" />
                            </div>

                            <div class="secret-box">
                                <p class="secret-box__label">Codigo manual (respaldo):</p>
                                <div class="secret-box__content">
                                    <code class="secret-box__code">{{ totpSecret }}</code>
                                    <button type="button" class="secret-box__btn" @click="copySecret" :aria-label="copied ? 'Codigo copiado' : 'Copiar codigo secreto'">
                                        {{ copied ? 'Copiado!' : 'Copiar' }}
                                    </button>
                                </div>
                            </div>

                            <div class="otp-group otp-group--6">
                                <input v-for="(digit, index) in totpOtpDigits" :key="'totp-setup-' + index" :ref="el => { if (el) totpInputRefs[index] = el }" type="text" maxlength="1" class="otp-input" :value="totpOtpDigits[index]" @input="handleTotpOtpInput($event, index)" @keydown="handleTotpOtpKeydown($event, index)" @paste="handleTotpOtpPaste($event)" inputmode="numeric" autocomplete="one-time-code" :aria-label="'Digito ' + (index + 1) + ' de 6'"/>
                            </div>

                            <button type="button" class="twofa-btn" :disabled="loading || totpOtpCode.length !== 6" @click="verifyAndEnableTotp">
                                <span v-if="loading" class="twofa-spinner"></span>
                                {{ loading ? 'Verificando...' : 'Verificar y Activar' }}
                            </button>
                        </template>
                    </template>

                    <template v-else>
                        <p class="twofa-info">Ingresa el codigo de 6 digitos de tu app Authenticator</p>
                        <div class="otp-group otp-group--6">
                            <input v-for="(digit, index) in totpOtpDigits" :key="'totp-verify-' + index" :ref="el => { if (el) totpInputRefs[index] = el }" type="text" maxlength="1" class="otp-input" :value="totpOtpDigits[index]" @input="handleTotpOtpInput($event, index)" @keydown="handleTotpOtpKeydown($event, index)" @paste="handleTotpOtpPaste($event)" inputmode="numeric" autocomplete="one-time-code"  :aria-label="'Digito ' + (index + 1) + ' de 6'"/>
                            
                        </div>

                        <button type="button" class="twofa-btn" :disabled="loading || totpOtpCode.length !== 6" @click="verifyTotpCode">
                            <span v-if="loading" class="twofa-spinner"></span>
                            {{ loading ? 'Verificando...' : 'Verificar' }}
                        </button>
                    </template>
                </div>
            </div>

            <transition name="twofa-fade">
                <div v-if="errorMessage" class="twofa-alert twofa-alert--error">
                    <p class="twofa-alert__text">- {{ errorMessage }}</p>
                </div>
            </transition>

            <transition name="twofa-fade">
                <div v-if="successMessage" class="twofa-alert twofa-alert--success">
                    <p class="twofa-alert__text">- {{ successMessage }}</p>
                </div>
            </transition>

            <button type="button" class="twofa-back-link" @click="goBackToLogin">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="m12 19-7-7 7-7"/>
                    <path d="M19 12H5"/>
                </svg>
                Volver al inicio de sesion
            </button>
        </div>
    </div>
</template>

<script>
    import { ref, reactive, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
    import { useRouter } from 'vue-router';
    import axios from 'axios';
    import { assetUrl } from '@/common/constants';

    export default {
        name: 'TwoFactorVerification',
        methods: {
            assetUrl
        },
        setup() {
            const router = useRouter();
            const selectedMethod = ref('email');
            const loading = ref(false);
            const errorMessage = ref('');
            const successMessage = ref('');
            const tempToken = ref('');
            const userId = ref('');
            const userEmail = ref('');
            const userName = ref('');
            const maskedEmail = ref('');
            const hasAuthenticator = ref(false);
            const emailOtpDigits = reactive(Array(8).fill(''));
            const emailInputRefs = reactive([]);
            const emailCodeSent = ref(false);
            const resendCountdown = ref(0);
            let resendInterval = null;
            const totpOtpDigits = reactive(Array(6).fill(''));
            const totpInputRefs = reactive([]);
            const qrCodeUrl = ref('');
            const totpSecret = ref('');
            const copied = ref(false);

            const emailOtpCode = computed(() => emailOtpDigits.join(''));
            const totpOtpCode = computed(() => totpOtpDigits.join(''));
            const formattedCountdown = computed(() => {
                const mins = Math.floor(resendCountdown.value / 60);
                const secs = resendCountdown.value % 60;
                return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
            });

            onMounted(async () => {
                tempToken.value = sessionStorage.getItem('tempToken') || '';
                userId.value = sessionStorage.getItem('2fa_userId') || '';
                userEmail.value = sessionStorage.getItem('2fa_userEmail') || '';
                userName.value = sessionStorage.getItem('2fa_userName') || '';
                maskedEmail.value = sessionStorage.getItem('2fa_maskedEmail') || '';

                if (!tempToken.value || !userId.value) {
                    router.push('/login');
                    return;
                }

                try {
                    const response = await axios.get(`${process.env.VUE_APP_NEST_2FA_STATUS}`, {
                        params: { userId: userId.value },
                        headers: { Authorization: `Bearer ${tempToken.value}` },
                    });

                    hasAuthenticator.value = response.data.hasAuthenticator || false;

                    if (response.data.hasAuthenticator && response.data.method === 'authenticator') {
                        selectedMethod.value = 'authenticator';
                    }
                } catch (error) {
                  console.error('Error al consultar estado 2FA:', error);
                }
            });

            onBeforeUnmount(() => {
                if (resendInterval) {
                    clearInterval(resendInterval);
                }
            });

            // ============================================
            // CAMBIAR METODO
            // ============================================
            const switchMethod = (method) => {
                selectedMethod.value = method;
                errorMessage.value = '';
                successMessage.value = '';
            };

            // ============================================
            // METODO: EMAIL - Enviar codigo
            // ============================================
            const sendEmailCode = async () => {
                loading.value = true;
                errorMessage.value = '';
                successMessage.value = '';

                try {
                    const response = await axios.post(`${process.env.VUE_APP_NEST_2FA_SEND_CODE}`,{
                        userId: Number(userId.value),
                        email: userEmail.value,
                        userName: userName.value,
                    },{
                        headers: { Authorization: `Bearer ${tempToken.value}` },
                    },);

                    emailCodeSent.value = true;
                    maskedEmail.value = response.data.maskedEmail || maskedEmail.value;
                    successMessage.value = 'Codigo enviado a su correo electronico';
                    startResendCountdown();

                    await nextTick();
                    if (emailInputRefs[0]) {
                        emailInputRefs[0].focus();
                    }

                    setTimeout(() => {
                        successMessage.value = '';
                    }, 4000);
                } catch (error) {
                    errorMessage.value =
                    error.response?.data?.message || 'Error al enviar el codigo. Intente de nuevo.';
                } finally {
                    loading.value = false;
                }
            };

            // ============================================
            // METODO: EMAIL - Verificar codigo
            // ============================================
            const verifyEmailCode = async () => {
                loading.value = true;
                errorMessage.value = '';
                successMessage.value = '';

                try {
                    const response = await axios.post(`${process.env.VUE_APP_NEST_2FA_COMPLETE_EMAIL}`,{
                        tempToken: tempToken.value,
                        userId: Number(userId.value),
                        code: emailOtpCode.value,
                    },{
                        headers: { Authorization: `Bearer ${tempToken.value}` },
                    },);

                  if (response.data.ok) {
                      successMessage.value = 'Verificacion exitosa. Redirigiendo...';
                      completeAuthentication(response.data.token);
                  }
                } catch (error) {
                    errorMessage.value =
                    error.response?.data?.message || 'Codigo incorrecto. Intente de nuevo.';
                    clearEmailOtp();
                } finally {
                    loading.value = false;
                }
            };

            // ============================================
            // METODO: AUTHENTICATOR - Generar QR
            // ============================================
            const generateTotp = async () => {
                loading.value = true;
                errorMessage.value = '';

                try {
                    const response = await axios.post(`${process.env.VUE_APP_NEST_2FA_GENERATE_TOTP}`,{
                        userId: Number(userId.value),
                        email: userEmail.value,
                    },{
                        headers: { Authorization: `Bearer ${tempToken.value}` },
                    },);

                    qrCodeUrl.value = response.data.qrCodeDataUrl;
                    totpSecret.value = response.data.secret;
                } catch (error) {
                errorMessage.value =error.response?.data?.message || 'Error al generar el codigo QR.';
                } finally {
                  loading.value = false;
                }
            };

            // ============================================
            // METODO: AUTHENTICATOR - Verificar y activar (primera vez)
            // ============================================
            const verifyAndEnableTotp = async () => {
                loading.value = true;
                errorMessage.value = '';
                successMessage.value = '';

                try {
                    const response = await axios.post(`${process.env.VUE_APP_NEST_2FA_COMPLETE_TOTP_SETUP}`,{
                        tempToken: tempToken.value,
                        userId: Number(userId.value),
                        code: totpOtpCode.value,
                        secret: totpSecret.value,
                    },{
                        headers: { Authorization: `Bearer ${tempToken.value}` },
                    },);

                    if (response.data.ok) {
                        successMessage.value = 'Google Authenticator configurado exitosamente. Redirigiendo...';
                        completeAuthentication(response.data.token);
                    }
                } catch (error) {
                    errorMessage.value = error.response?.data?.message || 'Codigo incorrecto. Verifique en la aplicacion.';
                    clearTotpOtp();
                } finally {
                  loading.value = false;
                }
            };

            // ============================================
            // METODO: AUTHENTICATOR - Verificar codigo (ya configurado)
            // ============================================
            const verifyTotpCode = async () => {
                loading.value = true;
                errorMessage.value = '';
                successMessage.value = '';

                try {
                    const response = await axios.post(`${process.env.VUE_APP_NEST_2FA_COMPLETE_TOTP}`,{
                        tempToken: tempToken.value,
                        userId: Number(userId.value),
                        code: totpOtpCode.value,
                    },{
                        headers: { Authorization: `Bearer ${tempToken.value}` },
                    },);

                    if (response.data.ok) {
                        successMessage.value = 'Verificacion exitosa. Redirigiendo...';
                        completeAuthentication(response.data.token);
                    }
                } catch (error) {
                    errorMessage.value =
                    error.response?.data?.message || 'Codigo incorrecto. Intente de nuevo.';
                    clearTotpOtp();
                } finally {
                    loading.value = false;
                }
            };


            // ============================================
            // COMPLETAR AUTENTICACION
            // ============================================
            const completeAuthentication = async (token) => {
                localStorage.setItem('token', 'Bearer ' + token);
                document.cookie = `token=${token}; path=/; max-age=${8 * 60 * 60}; SameSite=Strict`;

                const userStr = sessionStorage.getItem('user');
                if (userStr) {
                    localStorage.setItem('user', userStr);
                }

                axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

                sessionStorage.removeItem('tempToken');
                sessionStorage.removeItem('2fa_userId');
                sessionStorage.removeItem('2fa_userEmail');
                sessionStorage.removeItem('2fa_userName');
                sessionStorage.removeItem('2fa_maskedEmail');
                sessionStorage.removeItem('user');

                await router.replace('/inicio').catch(e => console.log("Router error:", e));
            };

            // ============================================
            // INPUTS OTP - EMAIL (8 digitos)
            // ============================================
            const handleEmailOtpInput = (event, index) => {
                const value = event.target.value;
                if (/^\d$/.test(value)) {
                    emailOtpDigits[index] = value;
                    if (index < 7 && emailInputRefs[index + 1]) {
                        emailInputRefs[index + 1].focus();
                    }
                } else {
                    event.target.value = emailOtpDigits[index];
                }
            };

            const handleEmailOtpKeydown = (event, index) => {
                if (event.key === 'Backspace') {
                    if (emailOtpDigits[index]) {
                        emailOtpDigits[index] = '';
                    } else if (index > 0 && emailInputRefs[index - 1]) {
                        emailInputRefs[index - 1].focus();
                        emailOtpDigits[index - 1] = '';
                    }
                }
                if (event.key === 'ArrowLeft' && index > 0) {
                    emailInputRefs[index - 1].focus();
                }
                if (event.key === 'ArrowRight' && index < 7) {
                    emailInputRefs[index + 1].focus();
                }
            };

            const handleEmailOtpPaste = (event) => {
                event.preventDefault();
                const pastedData = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 8);
                for (let i = 0; i < pastedData.length && i < 8; i++) {
                    emailOtpDigits[i] = pastedData[i];
                }
                const nextIndex = Math.min(pastedData.length, 7);
                if (emailInputRefs[nextIndex]) {
                    emailInputRefs[nextIndex].focus();
                }
            };

            const clearEmailOtp = () => {
                for (let i = 0; i < 8; i++) {
                    emailOtpDigits[i] = '';
                }
                nextTick(() => {
                    if (emailInputRefs[0]) {
                        emailInputRefs[0].focus();
                    }
                });
            };

            // ============================================
            // INPUTS OTP - TOTP (6 digitos)
            // ============================================
            const handleTotpOtpInput = (event, index) => {
                const value = event.target.value;
                if (/^\d$/.test(value)) {
                    totpOtpDigits[index] = value;
                    if (index < 5 && totpInputRefs[index + 1]) {
                        totpInputRefs[index + 1].focus();
                    }
                } else {
                    event.target.value = totpOtpDigits[index];
                }
            };

            const handleTotpOtpKeydown = (event, index) => {
                if (event.key === 'Backspace') {
                    if (totpOtpDigits[index]) {
                        totpOtpDigits[index] = '';
                    } else if (index > 0 && totpInputRefs[index - 1]) {
                        totpInputRefs[index - 1].focus();
                        totpOtpDigits[index - 1] = '';
                    }
                }
                if (event.key === 'ArrowLeft' && index > 0) {
                    totpInputRefs[index - 1].focus();
                }
                if (event.key === 'ArrowRight' && index < 5) {
                    totpInputRefs[index + 1].focus();
                }
            };

            const handleTotpOtpPaste = (event) => {
                event.preventDefault();
                const pastedData = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
                for (let i = 0; i < pastedData.length && i < 6; i++) {
                    totpOtpDigits[i] = pastedData[i];
                }
                const nextIndex = Math.min(pastedData.length, 5);
                if (totpInputRefs[nextIndex]) {
                    totpInputRefs[nextIndex].focus();
                }
            };

            const clearTotpOtp = () => {
                for (let i = 0; i < 6; i++) {
                    totpOtpDigits[i] = '';
                }
                nextTick(() => {
                    if (totpInputRefs[0]) {
                      totpInputRefs[0].focus();
                    }
                });
            };

            // ============================================
            // UTILIDADES
            // ============================================
            const startResendCountdown = () => {
                resendCountdown.value = 120;
                if (resendInterval) {
                    clearInterval(resendInterval);
                }
                resendInterval = setInterval(() => {
                    resendCountdown.value--;
                    if (resendCountdown.value <= 0) {
                        clearInterval(resendInterval);
                        resendInterval = null;
                    }
                }, 1000);
            };

            const copySecret = async () => {
                try {
                    await navigator.clipboard.writeText(totpSecret.value);
                } catch {
                    const textarea = document.createElement('textarea');
                    textarea.value = totpSecret.value;
                    document.body.appendChild(textarea);
                    textarea.select();
                    document.execCommand('copy');
                    document.body.removeChild(textarea);
                }
                copied.value = true;
                setTimeout(() => {
                    copied.value = false;
                }, 2000);
            };

            const goBackToLogin = () => {
                sessionStorage.removeItem('tempToken');
                sessionStorage.removeItem('2fa_userId');
                sessionStorage.removeItem('2fa_userEmail');
                sessionStorage.removeItem('2fa_userName');
                sessionStorage.removeItem('2fa_maskedEmail');
                if (resendInterval) {
                    clearInterval(resendInterval);
                }
                router.push('/login');
            };

            return {
                selectedMethod,
                loading,
                errorMessage,
                successMessage,
                maskedEmail,
                hasAuthenticator,
                emailOtpDigits,
                emailInputRefs,
                emailCodeSent,
                emailOtpCode,
                resendCountdown,
                formattedCountdown,
                totpOtpDigits,
                totpInputRefs,
                totpOtpCode,
                qrCodeUrl,
                totpSecret,
                copied,
                switchMethod,
                sendEmailCode,
                verifyEmailCode,
                generateTotp,
                verifyAndEnableTotp,
                verifyTotpCode,
                copySecret,
                goBackToLogin,
                handleEmailOtpInput,
                handleEmailOtpKeydown,
                handleEmailOtpPaste,
                handleTotpOtpInput,
                handleTotpOtpKeydown,
                handleTotpOtpPaste,
                completeAuthentication
            };
        },
    };
</script>

<style lang="scss" scoped>
    @import "@/style/main.scss";

    // ============================================
    // COLORES DEL SISTEMA (Tesoreria del Tolima)
    // ============================================
    $teal-primary: #1a6b5a;
    $teal-light: #2d8f7b;
    $slate-secondary: #3d4f5f;
    $salmon-accent: #e8937a;
    $bg-light: #f8fafb;
    $border-color: #e2e8f0;
    $muted-bg: #f1f5f9;

    // ============================================
    // PAGE LAYOUT
    // ============================================
    .twofa-page {
        min-height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: $bg-light;
        padding: 1rem;
        position: relative;

        &__bg {
            position: fixed;
            inset: 0;
            overflow: hidden;
            pointer-events: none;
        }

        &__bg-circle {
            position: absolute;
            border-radius: 50%;

            &--teal {
                top: -8rem;
                right: -8rem;
                width: 500px;
                height: 500px;
                background-color: rgba($teal-primary, 0.08);
            }

            &--salmon {
                bottom: -10rem;
                left: -10rem;
                width: 600px;
                height: 600px;
                background-color: rgba($salmon-accent, 0.06);
            }
        }
    }

    .twofa-container {
        position: relative;
        width: 100%;
        max-width: 420px;
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    // ============================================
    // ESCUDO
    // ============================================
    .twofa-shield {
        margin-bottom: 1rem;

        &__img {
            width: 96px;
            height: auto;
        }
    }

    // ============================================
    // ENCABEZADO
    // ============================================
    .twofa-heading {
        font-size: 1rem;
        color: $teal-primary;
        margin-bottom: 1.5rem;
        font-weight: 600;
    }

    // ============================================
    // CARD
    // ============================================
    .twofa-card {
        width: 100%;
        background-color: #ffffff;
        border-radius: 8px;
        box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
        border: 1px solid $border-color;
        overflow: hidden;
    }

    // ============================================
    // TABS
    // ============================================
    .twofa-tabs {
      display: flex;
      border-bottom: 1px solid $border-color;

      &__item {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.4rem;
          padding: 0.875rem 0.75rem;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          border: none;
          background-color: transparent;
          color: rgba($slate-secondary, 0.6);
          border-bottom: 2px solid transparent;
          transition: all 0.25s ease;

          &:hover:not(.twofa-tabs__item--active) {
              color: $slate-secondary;
              background-color: rgba($muted-bg, 0.5);
          }

          &--active {
              color: $teal-primary;
              border-bottom-color: $teal-primary;
              background-color: rgba($teal-primary, 0.05);
          }
      }

      &__label--full {
          display: none;

          @media (min-width: 480px) {
              display: inline;
          }
      }

      &__label--short {
          display: inline;

          @media (min-width: 480px) {
              display: none;
          }
      }
    }

    // ============================================
    // CUERPO
    // ============================================
    .twofa-body {
        padding: 1.5rem;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 1.25rem;
    }

    // ============================================
    // TEXTO INFORMATIVO
    // ============================================
    .twofa-info {
        font-size: 0.875rem;
        color: $slate-secondary;
        text-align: center;
        line-height: 1.6;

        strong {
            color: $teal-primary;
            font-weight: 700;
        }
    }

    // ============================================
    // BOTON PRINCIPAL
    // ============================================
    .twofa-btn {
        width: 100%;
        padding: 0.75rem 1rem;
        border-radius: 4px;
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        font-weight: 600;
        font-size: 0.875rem;
        color: #ffffff;
        background: linear-gradient(to right, $teal-primary, $teal-light);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        transition: all 0.2s ease;

        &:hover:not(:disabled) {
            background: linear-gradient(to right, darken($teal-primary, 5%), darken($teal-light, 5%));
        }

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
    }

    // ============================================
    // INPUTS OTP
    // ============================================
    .otp-group {
        display: flex;
        justify-content: center;
        gap: 0.5rem;
        width: 100%;

        &--6 {
            .otp-input {
                width: 3rem;
                height: 3.5rem;
                font-size: 1.25rem;
            }
        }
    }

    .otp-input {
        width: 2.25rem;
        height: 2.75rem;
        text-align: center;
        font-size: 1rem;
        font-weight: 700;
        color: $teal-primary;
        background-color: #ffffff;
        border: 2px solid rgba($slate-secondary, 0.3);
        border-radius: 4px;
        transition: border-color 0.2s, box-shadow 0.2s;

        &:focus {
            outline: none;
            border-color: $teal-primary;
            box-shadow: 0 0 0 3px rgba($teal-primary, 0.15);
        }
    }

    // ============================================
    // COUNTDOWN REENVIO
    // ============================================
    .twofa-countdown {
        font-size: 0.85rem;
        color: rgba($slate-secondary, 0.7);
        text-align: center;

        strong {
            color: $teal-primary;
            font-family: monospace;
            font-size: 1rem;
        }
    }

    // ============================================
    // QR
    // ============================================
    .qr-box {
        display: flex;
        justify-content: center;
        padding: 0.75rem;
        background-color: #ffffff;
        border: 2px solid rgba($slate-secondary, 0.2);
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

        &__img {
            width: 176px;
            height: 176px;
        }
    }

    // ============================================
    // SECRETO MANUAL
    // ============================================
    .secret-box {
        width: 100%;

        &__label {
            font-size: 0.75rem;
            color: rgba($slate-secondary, 0.6);
            text-align: center;
            margin-bottom: 0.5rem;
        }

        &__content {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
            padding: 0.625rem 0.75rem;
            background-color: $muted-bg;
            border: 1px solid $border-color;
            border-radius: 4px;
        }

        &__code {
            font-size: 0.75rem;
            font-weight: 600;
            letter-spacing: 1.5px;
            color: $teal-primary;
            word-break: break-all;
            font-family: monospace;
        }

        &__btn {
            padding: 0.375rem 0.625rem;
            font-size: 0.7rem;
            font-weight: 600;
            border: 1px solid $border-color;
            border-radius: 4px;
            cursor: pointer;
            background-color: #ffffff;
            color: $slate-secondary;
            white-space: nowrap;
            transition: all 0.2s;

            &:hover {
                background-color: $teal-primary;
                color: #ffffff;
                border-color: $teal-primary;
            }
        }
    }

    // ============================================
    // ALERTAS
    // ============================================
    .twofa-alert {
        width: 100%;
        margin-top: 1rem;
        padding: 0.75rem;
        border-radius: 4px;
        border-width: 2px;
        border-style: solid;

        &--error {
            background-color: #fef2f2;
            border-color: rgba(#ef4444, 0.3);
        }

        &--success {
            background-color: #d1fae5;
            border-color: rgba(#047857, 0.3);
        }

        &__text {
            font-size: 0.875rem;
            font-weight: 700;
        }

        &--error &__text {
            color: #991b1b;
        }

        &--success &__text {
            color: #047857;
        }
    }

    // ============================================
    // LINK VOLVER
    // ============================================
    .twofa-link-btn {
        background: none;
        border: none;
        color: $slate-secondary;
        text-decoration: underline;
        cursor: pointer;
        font-size: 0.85rem;
        padding: 0;
        transition: color 0.2s;

        &:hover {
            color: $teal-primary;
        }
    }

    .twofa-back-link {
        margin-top: 1.5rem;
        display: flex;
        align-items: center;
        gap: 0.375rem;
        background: none;
        border: none;
        color: $slate-secondary;
        text-decoration: underline;
        cursor: pointer;
        font-size: 0.85rem;
        padding: 0;
        transition: color 0.2s;

        &:hover {
            color: $teal-primary;
        }
    }

    // ============================================
    // SPINNER
    // ============================================
    .twofa-spinner {
        display: inline-block;
        width: 16px;
        height: 16px;
        border: 2px solid rgba(255, 255, 255, 0.4);
        border-top-color: #ffffff;
        border-radius: 50%;
        animation: twofa-spin 0.6s linear infinite;
    }

    @keyframes twofa-spin {
        to {
            transform: rotate(360deg);
        }
    }

    // ============================================
    // TRANSICIONES
    // ============================================
    .twofa-fade-enter-active,.twofa-fade-leave-active {
        transition: opacity 0.3s ease, transform 0.3s ease;
    }

    .twofa-fade-enter-from,.twofa-fade-leave-to {
        opacity: 0;
        transform: translateY(-8px);
    }

    // ============================================
    // RESPONSIVE
    // ============================================
    @media (max-width: 480px) {
        .otp-input {
            width: 2rem;
            height: 2.5rem;
            font-size: 0.875rem;
        }

        .otp-group--6 .otp-input {
            width: 2.5rem;
            height: 3rem;
            font-size: 1.125rem;
        }

        .qr-box__img {
            width: 144px;
            height: 144px;
        }
    }
</style>
